import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Separator } from './ui/separator';
import { Save, Upload, Trash2, Building2, Phone, Mail, Globe, FileImage, Check } from 'lucide-react';
import { toast } from 'sonner';

const SettingsView = () => {
  const fileInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Lade Einstellungen aus localStorage (Account-gebunden)
  const loadSettings = () => {
    try {
      return JSON.parse(localStorage.getItem('accountSettings') || '{}');
    } catch (e) {
      return {};
    }
  };
  
  const [firmendaten, setFirmendaten] = useState(() => {
    const saved = loadSettings();
    return {
      firmenname: saved.firmendaten?.firmenname || '',
      strasse: saved.firmendaten?.strasse || '',
      plz: saved.firmendaten?.plz || '',
      ort: saved.firmendaten?.ort || '',
      telefon: saved.firmendaten?.telefon || '',
      email: saved.firmendaten?.email || '',
      website: saved.firmendaten?.website || '',
      logo: saved.firmendaten?.logo || null
    };
  });

  const handleInputChange = (field, value) => {
    setFirmendaten(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFirmendaten(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFirmendaten(prev => ({ ...prev, logo: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    
    // Speichere in localStorage (Account-gebunden, nicht Projekt-gebunden)
    const settings = {
      firmendaten: firmendaten
    };
    localStorage.setItem('accountSettings', JSON.stringify(settings));
    
    // Trigger ein storage event um andere Komponenten zu informieren
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new CustomEvent('firmendatenUpdated', { detail: firmendaten }));
    
    // Zeige Toast-Benachrichtigung
    toast.success('Firmendaten wurden erfolgreich gespeichert!');
    
    setTimeout(() => setIsSaving(false), 500);
  };

  return (
    <div className="h-full overflow-auto p-6 bg-gray-50" data-testid="settings-view">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Einstellungen</h1>
            <p className="text-gray-500">Firmendaten und Logo für Schriftfelder</p>
          </div>
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" disabled={isSaving} data-testid="save-settings-btn">
            {isSaving ? <Check className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {isSaving ? 'Gespeichert!' : 'Speichern'}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Firmendaten
            </CardTitle>
            <CardDescription>
              Diese Daten werden im Schriftfeld des Stromlaufplans angezeigt
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="firmenname">Firmenname</Label>
                <Input
                  id="firmenname"
                  value={firmendaten.firmenname}
                  onChange={(e) => handleInputChange('firmenname', e.target.value)}
                  placeholder="z.B. Elektro Mustermann GmbH"
                  data-testid="input-firmenname"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="strasse">Straße und Hausnummer</Label>
                <Input
                  id="strasse"
                  value={firmendaten.strasse}
                  onChange={(e) => handleInputChange('strasse', e.target.value)}
                  placeholder="z.B. Musterstraße 123"
                  data-testid="input-strasse"
                />
              </div>
              
              <div>
                <Label htmlFor="plz">PLZ</Label>
                <Input
                  id="plz"
                  value={firmendaten.plz}
                  onChange={(e) => handleInputChange('plz', e.target.value)}
                  placeholder="z.B. 12345"
                  data-testid="input-plz"
                />
              </div>
              
              <div>
                <Label htmlFor="ort">Ort</Label>
                <Input
                  id="ort"
                  value={firmendaten.ort}
                  onChange={(e) => handleInputChange('ort', e.target.value)}
                  placeholder="z.B. Musterstadt"
                  data-testid="input-ort"
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="telefon" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telefon
                </Label>
                <Input
                  id="telefon"
                  value={firmendaten.telefon}
                  onChange={(e) => handleInputChange('telefon', e.target.value)}
                  placeholder="z.B. +49 123 456789"
                  data-testid="input-telefon"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  E-Mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={firmendaten.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="z.B. info@firma.de"
                  data-testid="input-email"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Website
                </Label>
                <Input
                  id="website"
                  value={firmendaten.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="z.B. www.firma.de"
                  data-testid="input-website"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileImage className="w-5 h-5" />
              Firmenlogo
            </CardTitle>
            <CardDescription>
              Laden Sie Ihr Firmenlogo hoch (PNG, JPG, SVG - max. 2MB)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                  data-testid="logo-upload-input"
                />
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-logo-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Logo hochladen
                  </Button>
                  
                  {firmendaten.logo && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveLogo}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="remove-logo-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Entfernen
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  Das Logo wird im Schriftfeld des Stromlaufplans und auf dem Titelblatt angezeigt.
                </p>
              </div>
              
              <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                {firmendaten.logo ? (
                  <img
                    src={firmendaten.logo}
                    alt="Firmenlogo"
                    className="max-w-full max-h-full object-contain p-2"
                    data-testid="logo-preview"
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <FileImage className="w-8 h-8 mx-auto mb-1" />
                    <span className="text-xs">Kein Logo</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn-bottom">
            <Save className="w-4 h-4 mr-2" />
            Alle Änderungen speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
